import React from "react";
import { Skeleton } from "@nextui-org/react";
import { useStaffHours } from "./useStaffHours";
import {
  Availability,
  AvailabilityStatusTypes,
  selectAvailabilities,
  updateWeeklyAvailability,
} from "app/redux/availabilitiesSlice";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

export interface StaffHoursProps {
  selectedEmployee: any;
}

export const StaffHours: React.FC<StaffHoursProps> = ({
  selectedEmployee,
}) => {
  const availabilities = useSelector(selectAvailabilities);
  const dispatch = useDispatch();

  const { isLoading } = useStaffHours({
    selectedEmployee,
  });

  const [error, setError] = React.useState<string | null>(null);

  const handleUpdateTime = async (
    availability: Availability,
    key: "timeStart" | "timeEnd",
    value: string
  ) => {
    try {
      await dispatch(
        updateWeeklyAvailability({
          employeeId: availability.availabilityableId,
          availability: {
            ...availability,
            [key]: value,
          },
        }) as any
      ).unwrap();
    } catch (err: any) {
      console.error("Error updating working hours:", err);
      toast.error(err?.code || err?.message || "Failed to update working hours");
      setError("An error occurred while updating working hours. Please try again.");
    }
  };

  const onScheduledSwitchToggle = async (
    availability: Availability,
    checked: boolean
  ) => {
    try {
      await dispatch(
        updateWeeklyAvailability({
          employeeId: availability.availabilityableId,
          availability: {
            ...availability,
            status: checked
              ? AvailabilityStatusTypes.SCHEDULED
              : AvailabilityStatusTypes.UNSCHEDULED,
          },
        }) as any
      ).unwrap();
    } catch (err: any) {
      console.error("Error updating working hours:", err);
      toast.error(err?.code || err?.message || "Failed to update working hours");
      setError("An error occurred while updating working hours. Please try again.");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Working Hours</h3>
        <p className="text-sm text-gray-500">
          These hours apply every week until changed.
        </p>

        {isLoading ? (
          <div className="flex flex-col gap-4 w-full justify-center items-center">
            {Array.from({ length: 7 }).map((_, index) => (
              <Skeleton className="rounded-lg h-14 w-full" key={index}>
                <div className="h-24 rounded-lg bg-default-300" />
              </Skeleton>
            ))}
          </div>
        ) : (
          availabilities?.availabilities?.map((a: Availability) => (
            <div
              key={a.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
            >
              {/* LEFT SIDE */}
              <div className="flex items-center space-x-4">
                <span className="w-20 text-sm font-medium text-gray-900 capitalize">
                  {a.day}
                </span>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={a.status === "scheduled"}
                    onChange={(e) => onScheduledSwitchToggle(a, e.target.checked)}
                    className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">Available</span>
                </label>
              </div>

              {/* RIGHT SIDE: TIME INPUTS */}
              {a.status === "scheduled" && (
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="time"
                      value={a.timeStart}
                      onChange={(e) => handleUpdateTime(a, "timeStart", e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg
                                 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="text-sm text-gray-500">to</span>
                    <input
                      type="time"
                      value={a.timeEnd}
                      onChange={(e) => handleUpdateTime(a, "timeEnd", e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg
                                 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
          ))
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
};
